import { debounce } from 'lodash'
import { Suspense, useCallback, useMemo } from 'react'
import { Virtuoso, type Components } from 'react-virtuoso'

import type { NotificationGroup } from 'src/shared/schemas/stream'

import { useGetNotifications } from '../api/use-get-notifications'
import NotificationItem from '../components/notification'
import AllNotificationsSkeleton from '../skeletons/all-notifications-skeleton'
import NotificationSkeleton from '../skeletons/notification-skeleton'
import { groupNotificationsByTimeframe } from '../util/group-notifications-by-timeframe'

import NotificationsEmptyView from './empty-view'

const ScrollSeekPlaceholder: Components['ScrollSeekPlaceholder'] = ({ height }) => (
  <div style={{ height }} className="py-2">
    <NotificationSkeleton />
  </div>
)

const AllNotificationsViewContent = () => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetNotifications({ limit: 10 })

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const debouncedLoadMore = useMemo(() => debounce(loadMore, 100), [loadMore])

  const allGroups = useMemo(() => data?.pages.flatMap((page) => page?.results || []) ?? [], [data?.pages])

  const groupedNotifications = useMemo(() => groupNotificationsByTimeframe(allGroups), [allGroups])

  const Footer = () => {
    if (isFetchingNextPage) {
      return (
        <div className="flex h-20 w-full justify-center">
          <NotificationSkeleton />
        </div>
      )
    }

    return null
  }

  const notifications = useMemo(() => groupedNotifications.flatMap((group) => group.groups), [groupedNotifications])

  if (notifications.length === 0) {
    return <NotificationsEmptyView />
  }

  return (
    <Virtuoso
      data={notifications}
      endReached={debouncedLoadMore}
      itemContent={(_, group: NotificationGroup) => (
        <NotificationItem activity={group.activities[0]} group={group} key={group.id} />
      )}
      components={{
        Footer,
        ScrollSeekPlaceholder,
      }}
      className="min-h-[390px]"
      style={{ width: '100%', scrollbarWidth: 'none' }}
      overscan={200}
      useWindowScroll={false} // Prevent using window scroll which can cause suspension issues
      increaseViewportBy={{ top: 150, bottom: 200 }} // Increase viewport to reduce suspense on scroll
    />
  )
}

const AllNotificationsView = () => {
  return (
    <Suspense fallback={<AllNotificationsSkeleton />}>
      <AllNotificationsViewContent />
    </Suspense>
  )
}

export default AllNotificationsView
